import { useState } from "react";
import { Button, Modal } from "react-bootstrap";
import { toast } from "react-toastify";
import { AdminCancelAccountDeletion } from "../../redux/auth-reducer/auth-service";
import { readApiMessage } from "../../utils/helper/helper";
import { formatLocalDate } from "../../utils/helper/accountDeletion";

/**
 * #234 (AC15): the Players lists' "Deletion" column, for the Web Admin. Player/GetAll returns
 * `deletionDueAt` and `deletionInProgress` to the Web Admin only. A scheduled deletion can be
 * cancelled on the player's behalf - one who cannot do it at sign-in themselves - until the sweep
 * has started deleting the account.
 *
 * @param {(row: object) => void} onCancel Opens the confirmation for that row.
 * @returns {object} A react-data-table-component column.
 */
export const deletionColumn = (onCancel) => ({
  name: "Deletion",
  selector: (row) => {
    if (row?.deletionInProgress) return <span>In progress</span>;
    if (!row?.deletionDueAt) return <span>-</span>;
    return (
      <div className="d-flex flex-wrap align-items-center gap-2 py-1">
        <span>Scheduled {formatLocalDate(row.deletionDueAt)}</span>
        <button className="tablebtn bg-dang" onClick={() => onCancel(row)}>
          Cancel deletion
        </button>
      </div>
    );
  },
  minWidth: "220px",
});

/**
 * The Web Admin's confirmation before cancelling a player's deletion request. Nothing is sent until
 * "Yes, cancel deletion". The player is not signed in by it; they get the cancellation email.
 *
 * onDone() is called whenever the server answered - also on a refusal (the sweep started deleting
 * the account meanwhile, say), which is shown as a toast, because the row has moved on either way.
 * With no reply the dialog stays open.
 *
 * @param {{player: object|null, onClose: () => void, onDone: () => void}} props
 */
export const CancelPlayerDeletionModal = ({ player, onClose, onDone }) => {
  const [busy, setBusy] = useState(false);

  const confirm = async () => {
    setBusy(true);
    const res = await AdminCancelAccountDeletion(player?.id);
    setBusy(false);

    if (res?.data?.status === true) {
      // `cancelled` is false when the account was already active - the player, or another admin,
      // cancelled it while this list was open - and then nothing changed and no email went out.
      if (res.data.cancelled) {
        toast.success("The deletion request has been cancelled. The player has been sent an email.");
      } else {
        toast.info("This player no longer has a deletion request. Nothing was changed.");
      }
      onDone();
      return;
    }

    if (res) {
      toast.error(readApiMessage(res));
      if (res.data?.status === false) onDone();
    }
  };

  return (
    <Modal show={!!player} centered onHide={busy ? undefined : onClose}>
      <Modal.Header className="promo-code-header promo-close" closeButton={!busy}>
        <Modal.Title>
          <b>Cancel deletion</b>
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p>
          Cancel the deletion request of{" "}
          <b>
            {player?.firstName} {player?.lastName}
          </b>{" "}
          ({player?.emailId}), scheduled for {formatLocalDate(player?.deletionDueAt)}?
        </p>
        <p className="mb-0">
          Do this only after verifying the player&apos;s identity. Their account becomes active again
          and they are sent an email about it.
        </p>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onClose} disabled={busy}>
          No
        </Button>
        <Button variant="danger" onClick={confirm} disabled={busy}>
          {busy ? "Cancelling..." : "Yes, cancel deletion"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};
